import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { glom } from '../../Signage/glom';
import { ReachAPI } from '../../ReachApi';
import { powerbi, models as pbiClientModels } from '../../../vendor/powerbi';
import { DateTime } from 'luxon';

import {
    PowerBIWidgetConfigurationDetails,
    PowerBIWidgetConfiguration,
    ResourceType,
    ReportPage,
    CloudType,
    ZodPowerBIWidgetConfigurationDetails,
} from './types';
import { WidgetType } from '../types';
import { Dashboard, Embed, Report } from 'powerbi-client';
import { IEmbedConfiguration } from 'embed';
import { Log } from '../../AuxillaryService/Log';
import { GetTokenAPISuccessResponse } from '../../ReachApi/powerbiTypes';

export class PowerBIWidget extends WidgetWithScheduler {
    type: WidgetType.POWERBI;
    declare config: PowerBIWidgetConfigurationDetails;

    /**
     * How often the data is refreshed in seconds
     */
    #dataRefreshInterval: number;

    /**
     * Last time the data was refreshed
     */
    #lastDataRefreshTime: DateTime | null;

    /**
     * Embed token for the resource
     */
    #embedToken: string | null;

    /**
     * Embed token expiry time (format: 'YYYY-MM-DDTHH:mm:ssZ')
     */
    #embedTokenExpiration: string | null;

    /**
     * Id of the resource being embedded
     */
    #resourceId: string;

    /**
     * Report or Dashboard
     */
    #resourceType: ResourceType;

    /**
     * Containing HTML element
     */
    #resourceContainer!: HTMLDivElement;

    /**
     * PowerBI resource instance - report or dashboard
     */
    #resource!: Report | Dashboard;

    /**
     * PowerBI filter expression
     * filters array as string
     */
    #filters: string;

    /**
     * PowerBI url filter - url filter (format: Table/Field eq 'value')
     */
    #urlFilter: string;

    /**
     * Pages in the report - pages
     */
    #reportPages: ReportPage[];

    /**
     * Pages slideshow
     */
    #autoPageSwitch: boolean;

    /**
     * Slideshow interval - Time in seconds
     */
    #slideshowInterval: number;

    /**
     * Slideshow interval, set when slideshow is started
     * Can be used to cancel slideshow
     */
    #slideshowIdentifier!: ReturnType<typeof setTimeout>;

    /**
     * The type of server the power bi application is hosted on
     */
    #cloudType: string;

    /**
     * Current position of the slideshow
     */
    #slideShowPageIndex: number;

    /**
     * Interval instance to check for browser upgrade modal
     */
    #dismissBrowserUpgradeCheckIdentifier: ReturnType<typeof setInterval> | null;

    constructor(widgetConfiguration: PowerBIWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.type = WidgetType.POWERBI;
        this.schedulerRunInterval = 60;

        this.#dataRefreshInterval = 3600;
        this.#reportPages = [];
        this.#slideshowInterval = 60;
        this.#cloudType = CloudType.General;

        this.#resourceId = this.config.resource_id;

        this.#resourceType = this.config.resource_type;

        this.#dataRefreshInterval = this.config.data_refresh_interval || this.#dataRefreshInterval;

        this.#autoPageSwitch = this.config.auto_page_switch || false;

        this.#cloudType = this.config.cloud_type || this.#cloudType;

        //assigning empty array as default value so that JSON.parse does not
        //throw exception
        this.#filters = this.config.filters || '[]';
        this.#urlFilter = this.config.url_filter || '';

        if (this.config.pages) {
            this.#reportPages = JSON.parse(this.config.pages) || this.#reportPages;
        }

        this.#slideshowInterval = this.config.pages_switch_interval;

        this.#lastDataRefreshTime = null;
        this.#embedToken = null;
        this.#embedTokenExpiration = null;
        this.#slideShowPageIndex = 0;
        this.#dismissBrowserUpgradeCheckIdentifier = null;
    }

    async initialize(): Promise<void> {
        await super.initialize();
    }

    protected validateConfigurationDetails(
        powerbiWidgetConfigurationDetails: PowerBIWidgetConfigurationDetails
    ): PowerBIWidgetConfigurationDetails {
        return ZodPowerBIWidgetConfigurationDetails.parse(powerbiWidgetConfigurationDetails);
    }

    async getReadyForDisplay(): Promise<void> {
        try {
            await this.getEmbedToken();
            await this.createPowerBIContainer();
            await this.renderPowerBI();
            this.#dismissBrowserUpgradeCheckIdentifier = setInterval(
                () => this.dismissBrowserUpgrade(),
                5 * 1000
            );
        } catch (error) {
            Log.Error(`PowerBI: Failed to get ready for display`, null, __fileline, error);
        }
    }

    /**
     * Get embed token used to render dashboard
     * @throws {Error} when token retrieval fails
     */
    async getEmbedToken(): Promise<void> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new Error('Player uuid or security token must be set');
        }
        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );
        const data: GetTokenAPISuccessResponse = await ReachAPI.powerbi.getToken(security, this.id);

        if (data.textStatus === 'success' && data.data.token.embed_token) {
            this.#embedToken = data.data.token.embed_token;
            this.#embedTokenExpiration = data.data.token.expiration;
            this.hasContentToPlay = true;
        } else {
            this.hasContentToPlay = false;
            throw new Error('Failed to get token.');
        }
    }

    /**
     * Adds container HTML element to the DOM
     */
    async createPowerBIContainer(): Promise<HTMLDivElement> {
        const element = document.createElement('div');
        element.setAttribute('id', `powerbi_${this.id}`);
        element.setAttribute('style', 'width: 100%; height: 100%;');
        this.wrapperElement.appendChild(element);
        this.#resourceContainer = element;
        return element;
    }

    /**
     * Renders dashboard
     */
    async renderPowerBI(): Promise<void> {
        const config: IEmbedConfiguration = await this.getConfig();
        const embed: Embed = powerbi.embed(this.#resourceContainer, config);

        // narrow type of embed to report or dashboard
        if (this.isReport(embed) || this.isDashboard(embed)) {
            this.#resource = embed;
        }

        // .off removes a given event handler if it exists.
        this.#resource.off('loaded');

        // .on will add an event handler which prints to Log
        this.#resource.on('loaded', async () => {
            Log.Info('PowerBI: PowerBI loaded', null, __fileline);
            this.#lastDataRefreshTime = DateTime.local();
            await this.configureReportPages();
        });

        this.#resource.on('error', () => {
            Log.Error(
                'PowerBI: PowerBI on resource error, resetting token and re-rendering.',
                null,
                __fileline
            );
            this.#resource.off('error');
            this.resetToken(true);
        });
    }

    /**
     * Prepares config object for dashboard render
     * @see https://github.com/Microsoft/PowerBI-JavaScript/wiki/Embed-Configuration-Details
     */
    async getConfig(): Promise<IEmbedConfiguration> {
        const resourceId = this.#resourceId;
        if (resourceId === null) {
            throw new Error('Resource id is not set');
        }
        let embedUrl =
            this.#cloudType === CloudType.Government
                ? 'https://app.powerbigov.us/' + this.#resourceType + 'Embed'
                : 'https://app.powerbi.com/' + this.#resourceType + 'Embed';

        if (this.#urlFilter) {
            embedUrl += '?filter=' + this.#urlFilter;
        }

        let filters = [];
        try {
            if (this.#filters !== null) {
                filters = JSON.parse(this.#filters);
            }
        } catch (ex) {
            throw new Error('Could not parse PowerBI filter expression.');
        }

        return {
            type: this.#resourceType,
            tokenType: pbiClientModels.TokenType.Embed,
            accessToken: this.#embedToken || '',
            embedUrl: embedUrl,
            id: resourceId,
            pageView: 'fitToWidth',
            filters: filters,
            settings: {
                //Hides filter pane
                filterPaneEnabled: false,

                //Hides and Disables page navigation
                navContentPaneEnabled: false,
            },
        };
    }

    /**
     * Configure slide show for pages
     */
    async configureReportPages(): Promise<void> {
        /**
         * Make sure the resource is not null
         * Only reports have pages
         */
        if (
            this.#resource === null ||
            this.#resourceType !== ResourceType.Report ||
            this.#autoPageSwitch !== true
        ) {
            return;
        }

        const selectedPages = PowerBIWidget.getSelectedPages(this.#reportPages);
        if (selectedPages.length === 1) {
            Log.Info(`PowerBI: Setting page ${selectedPages[0].name} as active.`, null, __fileline);

            if (this.#resource instanceof Report) {
                await this.#resource.page(selectedPages[0].name).setActive();
            }
        } else if (selectedPages.length > 1) {
            await this.startPagesSlideshow();
        }
    }

    /**
     * Filter selected pages
     */
    private static getSelectedPages(pages: ReportPage[]): ReportPage[] {
        return pages.filter((page) => page.selected);
    }

    /**
     * Start page slide show
     */
    async startPagesSlideshow(): Promise<void> {
        //@todo: validate pages - do the pages exist
        if (!this.isReport(this.#resource)) {
            throw new Error('Resource is not a report. Only reports can switch pages.');
        }

        const selectedPages = PowerBIWidget.getSelectedPages(this.#reportPages);

        await this.#resource.page(selectedPages[this.#slideShowPageIndex].name).setActive();
        if (this.#slideShowPageIndex === selectedPages.length - 1) {
            this.#slideShowPageIndex = 0;
        } else {
            this.#slideShowPageIndex++;
        }

        this.#slideshowIdentifier = setTimeout(
            this.startPagesSlideshow.bind(this),
            this.#slideshowInterval * 1000
        );
    }

    /**
     * Asserts if the resource is a report
     */
    isReport(resource: Embed): resource is Report {
        return resource instanceof Report;
    }

    /**
     * Asserts if the resource is a dashboard
     */
    isDashboard(resource: Embed): resource is Dashboard {
        return resource instanceof Dashboard;
    }

    /**
     * Check if token is valid
     */
    isTokenValid(): boolean {
        if (this.#embedTokenExpiration === null) {
            return false;
        }

        const expiration = DateTime.fromISO(this.#embedTokenExpiration);
        if (expiration.diffNow('minutes').minutes < 10) {
            Log.Info(
                `PowerBI: Expiration in ${expiration.diffNow('minutes').minutes} minutes.
                Expiration is set at ${expiration.toSQL()}`,
                null,
                __fileline
            );
            return false;
        }

        Log.Info('PowerBI: Token is valid till ' + expiration.toSQL(), null, __fileline);
        return true;
    }

    /**
     * Gets new token and sets it to the resource
     * @param {boolean} renderPowerBI - When true, will call renderPowerBI after setting
     * the new access token. This is to recover from a resource error event.
     * @returns {Promise}
     */
    async resetToken(renderPowerBI = false): Promise<boolean> {
        try {
            await this.getEmbedToken();
            if (!this.#embedToken) {
                throw new Error('Token not initialized.');
            }
            await this.#resource.setAccessToken(this.#embedToken);

            if (renderPowerBI) {
                await this.renderPowerBI();
            }

            Log.Info('PowerBI: Token successfully reset.', null, __fileline);
            return true;
        } catch (error) {
            Log.Info('Failed to reset token.', null, __fileline, error);
            throw new Error('Failed to reset token.');
        }
    }

    /**
     * Refreshes resource data
     */
    async refreshData(): Promise<void> {
        //make sure the resource is not null
        if (!this.isReport(this.#resource) && !this.isDashboard(this.#resource)) {
            return;
        }

        if (this.#lastDataRefreshTime) {
            if (
                !(
                    this.#lastDataRefreshTime.plus({ seconds: this.#dataRefreshInterval }) >
                    DateTime.local()
                )
            ) {
                Log.Info('PowerBI: Refreshing Data', null, __fileline);

                // Only reports can be refreshed, dashboards cannot be refreshed
                // Reload the whole embed if it is dashboard
                if (this.#resourceType === ResourceType.Report && this.isReport(this.#resource)) {
                    await this.#resource.refresh();
                } else if (
                    this.#resourceType === ResourceType.Dashboard &&
                    this.isDashboard(this.#resource)
                ) {
                    await this.#resource.reload();
                }
                this.#lastDataRefreshTime = DateTime.local();
            }
        } else {
            Log.Info('PowerBI: Refreshing Data', null, __fileline);
            this.#lastDataRefreshTime = DateTime.local();
            if (this.#resourceType === ResourceType.Report && this.isReport(this.#resource)) {
                await this.#resource.refresh();
            } else if (this.#resourceType === ResourceType.Dashboard) {
                await this.#resource.reload();
            }
        }
    }

    /**
     * Scheduler Action
     * Check tokens and refresh data
     */
    async schedulerAction(): Promise<void> {
        //Check token validity and refresh report
        if (!this.isTokenValid()) {
            try {
                /**
                 * Do not try to refresh data if token refresh failed
                 * The page shows errors, if refreshed with invalid token
                 */
                if (await this.resetToken()) {
                    await this.refreshData();
                }
            } catch (error) {
                Log.Info('Failed to refresh data.', null, __fileline, error);
                throw new Error('Failed to refresh data.');
            }
        } else {
            await this.refreshData();
        }
    }

    async stop(): Promise<void> {
        super.stop();
        if (this.#slideshowIdentifier) {
            clearTimeout(this.#slideshowIdentifier);
        }
        if (this.#dismissBrowserUpgradeCheckIdentifier) {
            clearInterval(this.#dismissBrowserUpgradeCheckIdentifier);
        }
        this.#slideShowPageIndex = 0;
    }

    /**
     * This method gives a temporary solution to the issue where an upgrade browser message pops up
     * We are clicking the "Not now" button programmatically to dismiss it.
     * This function should be removed we we have upgraded our browser version to 94 or higher.
     */
    dismissBrowserUpgrade(): void {
        try {
            /* Get the div element */
            const powerbiDiv = document.getElementById(`widget_${this.id}`);

            /* Find the iframe element within the div */
            const iframe = powerbiDiv?.querySelector('iframe');

            /* Get the iframe's document */
            const iframeWindow = iframe?.contentWindow;
            const iframeDocument = iframe?.contentDocument || iframe?.contentWindow?.document;

            if (!iframeWindow || !iframeDocument) {
                Log.Error(`PowerBI: Could not find Power BI iframe node`, null, __fileline);
                return;
            }

            /* Find "Not now" button and click it */
            const allButtons = iframeDocument.querySelectorAll('button');
            const notNowButton = Array.from(allButtons).find(
                (button) => button.innerText === 'Not now'
            );
            if (notNowButton) {
                notNowButton.click();
                Log.Info('PowerBI: Not now button clicked', null, __fileline);
            }

            /* Add localstorage value, so that the modal does not show up again */
            const dontShowModal = iframeWindow.localStorage.getItem('dontShowDeprecationDialog');
            if (dontShowModal !== 'true') {
                iframeWindow.localStorage.setItem('dontShowDeprecationDialog', 'true');
                Log.Info(
                    'PowerBI: localstorage value dontShowDeprecationDialog set to true',
                    null,
                    __fileline
                );
            }
        } catch (error) {
            Log.Error(`PowerBI: Failed to dismiss deprecation modal`, null, __fileline, error);
        }
    }
}
